import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { registerPlugin } from "@capacitor/core";
import { Store } from "@ngrx/store";
import { LocationTrackingDto } from "src/app/tracking/interfaces/location_tracking";
import {
  startTracking,
  upsertTracking,
  replaceTrackings,
  stopTracking,
} from "src/app/tracking/ngrx/tracking.actions";
import { Platform, ToastController } from "@ionic/angular";
import { LocalNotifications } from "@capacitor/local-notifications";
import { BehaviorSubject, firstValueFrom } from "rxjs";

import {
  BackgroundGeolocationPlugin,
  Location,
} from "@capacitor-community/background-geolocation";
import { selectTrackingByAppointmentId } from "src/app/tracking/ngrx/tracking.selectors";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>(
  "BackgroundGeolocation"
);

@Injectable({
  providedIn: "root",
})
export class GmapService {
  private locationSubject = new BehaviorSubject<Location | null>(null);
  public location$ = this.locationSubject.asObservable();

  trackingId: any;
  isTracking: boolean = false;

  temporaryWatcherId: any;

  constructor(
    private platform: Platform,
    private toastController: ToastController,
    private store: Store
  ) {}

  loadGoogleMaps(): Promise<any> {
    const win = window as any;
    const gModule = win.google;
    if (gModule && gModule.maps) {
      return Promise.resolve(gModule.maps);
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://maps.googleapis.com/maps/api/js?key=" +
        environment.googleMapsApiKey +
        "&callback=Function.prototype";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
      script.onload = () => {
        const loadedGoogleModule = win.google;
        if (loadedGoogleModule && loadedGoogleModule.maps) {
          resolve(loadedGoogleModule.maps);
        } else {
          reject("Google Map SDK is not Available");
        }
      };
    });
  }

  removeGoogleMapScript() {
    const gModule = window["google"];
    if (gModule && gModule.maps) {
      delete window["google"];
    }
  }

  async snapToRoads(path: any) {
    const endpoint =
      "https://roads.googleapis.com/v1/snapToRoads?interpolate=false&path=" +
      path +
      "&key=" +
      environment.googleMapsApiKey;
    const response = await fetch(endpoint);

    const json = await response.json();
    return json.snappedPoints?.map((point) => ({
      lat: point.location.latitude,
      lng: point.location.longitude,
    }));
  }

  startTracking(appointment_id: number, duration: number) {
    CapacitorUpdater.setMultiDelay({
      delayConditions: [
        { kind: 'kill' }
      ]
    });
    this.startBackgroundWatcher(appointment_id, duration)
      .then((v) => {
        console.log("started background watcher", { v });
      })
      .catch((error) => {
        if (error.code == "UNIMPLEMENTED") {
          console.warn(
            "background watcher unimplemented, falling back to foreground watcher",
            {
              error,
            }
          );
        } else {
          console.error("background watcher: " + JSON.stringify(error));
        }
      });
  }

  private async checkAndRequestPermissions(): Promise<boolean> {
    // Check for necessary permissions
    const permissions = await LocalNotifications.checkPermissions();
    if (permissions.display !== "granted") {
      const newPermissions = await LocalNotifications.requestPermissions();
      if (newPermissions.display !== "granted") {
        return false;
      }
    }

    return true;
  }

  async startBackgroundWatcher(
    appointment_id: number,
    durationMinutes: number
  ) {
    if (this.platform.is("android")) {
      const hasPermissions = await this.checkAndRequestPermissions();
      if (!hasPermissions) {
        this.toastController
          .create({
            message:
              "Standortverfolgung kann nicht gestartet werden, da die erforderlichen Berechtigungen fehlen.",
            duration: 2000,
          })
          .then((toast) => toast.present());
        return;
      }
    }

    try {
      this.isTracking = true;
      this.store.dispatch(startTracking({ appointmentId: appointment_id }));

      // Start the background geolocation watcher
      this.trackingId = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: "Standortverfolgung aktiv. App öffnen zum Beenden.",
          backgroundTitle: "IGBO GPS Tracking",
          requestPermissions: false,
          stale: true,
          distanceFilter: 40
        },
        (location, error) => {
          if (error) {
            this.isTracking = false;
            if (error.code === "NOT_AUTHORIZED") {
              if (
                window.confirm(
                  "Die App benötigt Zugriff auf deinen Standort.\n\n" +
                    "Einstellungen öffnen?"
                )
              ) {
                BackgroundGeolocation.openSettings();
              }
            } else {
              console.log("Error: " + error.code + error.message);
              console.error(error, { error });
            }
          } else if (!location) {
            this.isTracking = false;
            console.warn("Location missing", { location });
          } else {
            const tracking = {
              lat: location?.latitude,
              lng: location?.longitude,
              appointment_id: appointment_id,
              timestamp: location?.time ?? Date.now(),
            } as LocationTrackingDto;

            this.store.dispatch(upsertTracking({ tracking }));
            this.locationSubject.next(location);
          }
        }
      );

      // Automatically stop tracking after the specified duration
      const durationMilliseconds = durationMinutes * 60 * 1000; // Convert minutes to milliseconds
      setTimeout(async () => {
        await this.stopTracking(appointment_id); // Stop tracking
      }, durationMilliseconds);
    } catch (error) {
      this.isTracking = false;
      this.toastController
        .create({
          message:
            "Standort konnte nicht verfolgt werden. Versichere dich, dass Standortdienste aktiviert sind.",
          duration: 2000,
        })
        .then((toast) => toast.present());
    }
  }

  async getBackgroundLocation(): Promise<Location | undefined> {
    if (
      this.platform.is("android") &&
      !(await this.checkAndRequestPermissions())
    ) {
      return;
    } else if (!this.platform.is("capacitor")) {
      this.toastController
        .create({
          message:
            "Die Standortverfolgung kann in der Webversion nicht aktiviert werden.",
          duration: 2000,
        })
        .then((toast) => toast.present());
      return;
    }
    return new Promise(async (resolve, reject) => {
      let last_location: Location | undefined;

      this.temporaryWatcherId = await BackgroundGeolocation.addWatcher(
        {
          requestPermissions: true,
          stale: true,
        },
        (location, error) => {
          if (error) {
            if (error.code === "NOT_AUTHORIZED") {
              if (
                window.confirm(
                  "Die App benötigt Zugriff auf deinen Standort.\n\n" +
                    "Einstellungen öffnen?"
                )
              ) {
                BackgroundGeolocation.openSettings();
              }
            } else {
              this.isTracking = false;
              this.toastController
                .create({
                  message:
                    "Standort konnte nicht verfolgt werden. Versichere dich, dass Standortdienste aktiviert sind.",
                  duration: 2000,
                })
                .then((toast) => toast.present());
              reject(error);
            }
          } else if (!location) {
            console.warn("Location missing", { location });
          } else {
            last_location = location;
            resolve(last_location);
          }
        }
      )
    });
  }

  async removeAllWatchers() {
    const tempWatcher = { id: await this.temporaryWatcherId };
    await BackgroundGeolocation.removeWatcher({ id: tempWatcher.id });

    const trackingWatcher = { id: await this.trackingId };
    await BackgroundGeolocation.removeWatcher({ id: trackingWatcher.id });
  }

  async stopTracking(appointmentId: number) {
    const opt = { id: await this.trackingId };
    await BackgroundGeolocation.removeWatcher({
      id: opt.id,
    });
    this.isTracking = false;

    const existingTrackings$ = this.store.select(selectTrackingByAppointmentId(appointmentId));

    const existingTrackings = await firstValueFrom(existingTrackings$)
    const gpsTrackings = existingTrackings.filter(tracking => tracking.comment === null);

    if (gpsTrackings && gpsTrackings.length > 0) {
      // Split the path into chunks
      const maxPointsPerRequest = 100;
      const chunkedPaths = [];
      for (let i = 0; i < gpsTrackings.length; i += maxPointsPerRequest) {
        chunkedPaths.push(gpsTrackings.slice(i, i + maxPointsPerRequest));
      }

      const accumulatedSnappedPaths = [];

      // Process each chunk separately
      for (const chunk of chunkedPaths) {
        const pathParameter = chunk
          .map((location) => `${location.lat},${location.lng}`)
          .join("|");

        const snappedPath = await this.snapToRoads(pathParameter);

        // Accumulate the snapped path for this chunk
        accumulatedSnappedPaths.push(...snappedPath);
      }

      console.log("accumulatedSnappedPaths", { accumulatedSnappedPaths });

      const newTrackings = accumulatedSnappedPaths.map(
        (snappedPoint, index) => ({
          appointment_id: appointmentId,
          lat: snappedPoint.lat,
          lng: snappedPoint.lng,
          timestamp: new Date().getTime() + index * 1000,
        })
      );

      // Dispatch an action to update the tracking points in the database
      this.store.dispatch(replaceTrackings({ oldTrackings: gpsTrackings, newTrackings }));
    }
    
    // Always dispatch stop tracking, regardless of whether there were trackings
    this.store.dispatch(stopTracking());
  }
}
